import UserAvatar from '../UserAvatar';
import ConfirmModal from '../ConfirmModal';
import { ChevronLeftIcon, CalendarIcon, ImageIcon, FileTextIcon, DeleteIcon } from './Icons';
import { TYPE_CONFIG, STATUS_OPTIONS, RETRO_COLUMNS } from '../../constants';
import { formatDate, findAssignee } from '../../utils';
import useSprintHistory from '../../hooks/useSprintHistory';
import './mobile.css';

export default function SprintHistoryMobile({ onBack, showToast, users, userProfile, projectId, projectType }) {
  const isRetro = projectType === 'retrospective';
  const activeGroups = isRetro
    ? RETRO_COLUMNS.map((c) => ({ value: c.status, label: c.title }))
    : STATUS_OPTIONS;

  const {
    sprints, loading, selectedSprint, setSelectedSprint, sprintItems, loadingItems,
    exporting, deleteTarget, setDeleteTarget, showDeleteAll, setShowDeleteAll,
    isAdmin, handleSelectSprint, handleDeleteSprint, handleDeleteAll, handleExport,
  } = useSprintHistory({ showToast, projectId, userProfile });

  if (loading) {
    return (
      <div className="mobile-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-loading-spinner" />
        <p>Loading history...</p>
      </div>
    );
  }

  if (selectedSprint) {
    const closedByUser = findAssignee(users, selectedSprint.closedBy);

    return (
      <div className="mobile-page mobile-history-page">
        <div className="mobile-history-header">
          <button className="mobile-btn-icon" onClick={() => setSelectedSprint(null)}>
            <ChevronLeftIcon size={20} />
          </button>
          <h2>{selectedSprint.name}</h2>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="mobile-btn-icon" onClick={() => handleExport('mobile-history-board-export', 'png')} disabled={exporting || loadingItems} title="PNG">
              <ImageIcon size={16} />
            </button>
            <button className="mobile-btn-icon" onClick={() => handleExport('mobile-history-board-export', 'pdf')} disabled={exporting || loadingItems} title="PDF">
              <FileTextIcon size={16} />
            </button>
          </div>
        </div>
        <div className="mobile-history-detail-meta">
          <span className="mobile-history-detail-dates">
            <CalendarIcon size={12} /> {formatDate(selectedSprint.startDate)} - {formatDate(selectedSprint.endDate)}
          </span>
          {selectedSprint.closedAt && (
            <span className="mobile-history-detail-ended">
              Ended {formatDate(selectedSprint.closedAt)}
              {closedByUser && (
                <>
                  {' '}by <UserAvatar user={closedByUser} size={22} showStatus={false} />
                  <strong>{closedByUser.displayName}</strong>
                </>
              )}
            </span>
          )}
        </div>

        {loadingItems ? (
          <div className="mobile-loading" style={{ flex: 1 }}>
            <div className="mobile-loading-spinner" />
            <p>Loading items...</p>
          </div>
        ) : (
          <div className="mobile-sprint-body" id="mobile-history-board-export">
            {activeGroups.map((opt) => {
              const groupItems = sprintItems.filter((i) => i.status === opt.value);
              return (
                <div className="mobile-status-group" key={opt.value}>
                  <div className="mobile-status-header">
                    <h3>{opt.label}</h3>
                    <span className="mobile-status-count">{groupItems.length}</span>
                  </div>
                  {groupItems.map((item) => {
                    if (isRetro) {
                      return (
                        <div key={item.id} className="mobile-sprint-card" style={{ cursor: 'default' }}>
                          <div className="mobile-sprint-card-left">
                            <div>
                              <div className="mobile-sprint-card-title">{item.title}</div>
                              {item.description && (
                                <div className="mobile-sprint-card-desc">{item.description}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
                    const Icon = config.icon;
                    const assignee = findAssignee(users, item.assigneeId);
                    return (
                      <div key={item.id} className="mobile-sprint-card" style={{ cursor: 'default' }}>
                        <div className="mobile-sprint-card-left">
                          <span style={{ color: config.color }}><Icon size={18} /></span>
                          <div>
                            <div className="mobile-sprint-card-title">{item.title}</div>
                            {item.description && (
                              <div className="mobile-sprint-card-desc">{item.description}</div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {assignee && (
                            <UserAvatar user={assignee} size={24} showStatus={false} />
                          )}
                          {item.storyPoints != null && (
                            <span className="sprint-item-points">{item.storyPoints}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {groupItems.length === 0 && (
                    <div className="mobile-sprint-empty">No items</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mobile-page mobile-history-page">
      <div className="mobile-history-header">
        <button className="mobile-btn-icon" onClick={onBack}>
          <ChevronLeftIcon size={20} />
        </button>
        <h2>{isRetro ? 'Retro History' : 'Sprint History'}</h2>
        {isAdmin && sprints.length > 0 && (
          <button className="mobile-btn-icon" onClick={() => setShowDeleteAll(true)} title="Delete All" style={{ borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
            <DeleteIcon size={16} />
          </button>
        )}
      </div>

      {sprints.length === 0 ? (
        <div className="mobile-history-empty">
          {isRetro
            ? 'No completed retrospectives yet. Retros will appear here after they end.'
            : 'No closed sprints yet. Sprints will appear here after they end.'}
        </div>
      ) : (
        <div className="mobile-history-body">
          {sprints.map((sprint) => {
            const closedBy = findAssignee(users, sprint.closedBy);
            return (
              <div
                key={sprint.id}
                className="mobile-history-item"
                onClick={() => handleSelectSprint(sprint)}
              >
                <div className="mobile-history-item-name">{sprint.name}</div>
                <div className="mobile-history-item-dates">
                  <CalendarIcon size={12} /> {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
                </div>
                {sprint.closedAt && (
                  <div className="mobile-history-item-closed">
                    Ended {formatDate(sprint.closedAt)}
                    {closedBy && <> by {closedBy.displayName}</>}
                  </div>
                )}
                {isAdmin && (
                  <button
                    className="mobile-btn-icon mobile-history-item-delete"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(sprint.id); }}
                    title="Delete"
                  >
                    <DeleteIcon size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          message="Delete this sprint and all its items? This cannot be undone."
          onConfirm={handleDeleteSprint}
          onCancel={() => setDeleteTarget(null)}
          confirmLabel="Delete"
        />
      )}

      {showDeleteAll && (
        <ConfirmModal
          message="Delete ALL sprint history? This will permanently remove all closed sprints and their items."
          onConfirm={handleDeleteAll}
          onCancel={() => setShowDeleteAll(false)}
          confirmLabel="Delete All"
        />
      )}
    </div>
  );
}
